import AxiosInstance from '@/api/axios';
import { useAppSelector } from '@/stores/hooks';
import { RootState } from '@/stores/store';
import { previewTemplate } from '@/templates/previewTemplate';
import { getClientId } from '@/utils/helper';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../Base/Button';
import { Dialog } from '../Base/Headless';
import Lucide from '../Base/Lucide';
import Tippy from '../Base/Tippy';
import Loading from '../Loading/Loading';
import { Platform } from '@/utils/enum';

export interface IPreviewTemplate {
  _id?: string;
  header?: string;
  footer?: string | null;
  platform?: string;
  client?: string;
  arabicContent?: string | null;
  timezone?: string | null;
  subject?: string | null;
  name?: string | null;
  // ─── Viber only ───────────────────────────────────────────────────
  // An approved Viber template holds no body of ours: the text lives with
  // Viber and we address it by UUID. `viberMessageText` is the operator's
  // copy of that text (Templates -> "Message Text (for AI context)"), which
  // is also what the automation service sends to the LLM as `ai_message`.
  // It is the only faithful thing to preview for that kind.
  kind?: string;
  viberMessageText?: string | null;
}

export interface IPreviewTags {
  first_name?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  time?: string | null;
  location_logo?: string | null;
  location_name?: string | null;
  location_address?: string | null;
  club_address?: string | null;
  webUrl?: string | null;
  agent_name?: string | null;
  locationDetails?: string | null;
  subject?: string | null;
  logo_height?: string;
  branch?: string;
}

interface ITemplatePreviewProps {
  template?: IPreviewTemplate | null;
  agent: IPreviewTags;
  handleShowPreview?: () => void;
  variant?: 'plain';
  method?: string;
  platform: Platform;
}

const DefaultTemplate: IPreviewTemplate = {
  _id: 'test',
  header: `Hi {{first_name}}!\n\nThank you for scheduling a {{type}} at {{location_name}}!\n\n<b>Booking Details:</b>\n\n🗓 Date: {{start_date}}\n🕘 Time: {{time}}\n📍 Location: {{club_address}}\n\nWe look forward to seeing you at the club.\n\nThe {{location_name}} Team`,
  footer:
    '{{location_logo}}\n{{location_name}}, Copyright © 2025 \n{{location_address}}',
  platform: 'email',
  client: 'client',
  arabicContent: '',
  timezone: '',
  name: 'Email - Booking confirmation',
};
/**
 * Render an approved Viber template's reference text for preview, with its
 * variables left VISIBLE.
 *
 * We deliberately do not fill sample values here. For every other kind the
 * preview shows a rendered message, but an approved template's real text
 * lives with Viber — this is only the operator's manual copy of it, kept for
 * the LLM's ai_message. Showing it with placeholders intact is what an
 * operator needs: it lets them check their copy matches the approved
 * template variable-for-variable, which is exactly what Infobip validates
 * (a mismatched name is a hard delivery failure, code 7008).
 *
 * Mechanism: every variable is normalised to a SINGLE brace form. That is
 * how the Infobip portal writes them, and it is also the one form
 * previewTemplate#injectAgentData never substitutes — its regex requires
 * {{double}} braces. So the placeholders survive to the screen regardless of
 * what `agent` happens to supply, now or later.
 *
 *   {first_name}    {{first_name}}    {first_name>}   ->  {first_name}
 */
const viberReferenceToPreviewText = (text?: string | null): string => {
  if (!text) return '';
  return text.replace(
    /\{\{?\s*([A-Za-z0-9_]+)\s*[^}]*\}\}?/g,
    (_match, name: string) => `{${name}}`
  );
};

const TemplatePreview = ({
  template: incomingTemplate,
  agent,
  handleShowPreview,
  variant,
  method,
  platform,
}: ITemplatePreviewProps) => {
  const { branch_id } = useAppSelector(
    (state: RootState) => state.generalFilterSlice
  );
  const [showPreview, setShowPreview] = useState(false);
  const [template, setTemplate] = useState<IPreviewTemplate>(DefaultTemplate);
  const [isWhatsappLoading, setIsWhatsappLoading] = useState(false);
  const client = getClientId();
  const resolvedBranch = agent?.branch || branch_id;

  const getTwilioBody = (content: any): string => {
    if (!content?.types || typeof content.types !== 'object') {
      return '';
    }

    const textBody = content.types?.['twilio/text']?.body;
    if (typeof textBody === 'string') return textBody;

    const fallbackKeys = [
      'twilio/quick-reply',
      'twilio/call-to-action',
      'twilio/media',
    ];

    for (const key of fallbackKeys) {
      const body = content.types?.[key]?.body;
      if (typeof body === 'string') return body;
    }

    return '';
  };

  useEffect(() => {
    setTemplate(incomingTemplate || DefaultTemplate);
  }, [incomingTemplate]);

  const fetchLegacyTemplate = useCallback(async () => {
    const isWhatsappMethod = method?.trim().toLowerCase() === 'whatsapp';

    if (!isWhatsappMethod || !client || !resolvedBranch) {
      setIsWhatsappLoading(false);
      setTemplate(incomingTemplate || DefaultTemplate);
      return;
    }

    setIsWhatsappLoading(true);
    try {
      const { data } = await AxiosInstance.get(
        `/twilio/view/legacy/${client}/${resolvedBranch}`,
        {
          params: {
            platform: platform,
          },
        }
      );
      if (data?.data) {
        const twilioBody = getTwilioBody(data.data).trim();
        if (twilioBody) {
          setTemplate({
            _id: incomingTemplate?._id,
            name: incomingTemplate?.name || 'WhatsApp Template Preview',
            platform: platform || incomingTemplate?.platform,
            client: incomingTemplate?.client,
            header: twilioBody,
            footer: '',
            arabicContent: '',
            timezone: null,
            subject: '',
          });
          return;
        }
      }
    } catch (err) {
      console.error('Error fetching legacy template:', err);
    } finally {
      setIsWhatsappLoading(false);
    }

    setTemplate(incomingTemplate || DefaultTemplate);
  }, [client, incomingTemplate, method, platform, resolvedBranch]);

  useEffect(() => {
    if (!showPreview) return;
    fetchLegacyTemplate();
  }, [showPreview, fetchLegacyTemplate]);

  const handleClick = () => {
    setShowPreview(true);

    if (handleShowPreview) {
      handleShowPreview();
    }
  };

  const sanitizedContent = useMemo(() => {
    // Viber, like SMS, is a single plain-text body — it has no footer, so
    // rendering one would preview something that cannot be sent.
    const channel = method?.trim().toLowerCase();
    const isPlainTextMethod = channel === 'sms' || channel === 'viber';
    let templateForPreview = isPlainTextMethod
      ? { ...template, footer: '' }
      : template;

    // VIBER-APPROVED ONLY. Scoped on `channel === 'viber'` so no other
    // channel's preview can change: an approved Viber template's `header`
    // is empty by design, so previewing it rendered a blank box. Preview
    // the operator's reference copy instead — the same text the automation
    // service renders as the LLM's ai_message.
    if (channel === 'viber' && template?.kind === 'approved') {
      templateForPreview = {
        ...templateForPreview,
        header: viberReferenceToPreviewText(template?.viberMessageText),
      };
    }

    return {
      __html: DOMPurify.sanitize(previewTemplate(templateForPreview, agent)),
    };
  }, [template, agent, method]);

  return (
    <>
      <Tippy content='Preview Template'>
        <Button
          type='button'
          variant={variant ? 'plain' : 'outline-secondary'}
          onClick={() => handleClick()}
        >
          <Lucide icon='Eye' />
        </Button>
      </Tippy>
      {showPreview && (
        <Dialog
          size='xl'
          open={showPreview}
          onClose={() => setShowPreview(false)}
        >
          <Dialog.Panel className='p-4'>
            <Dialog.Title className='flex flex-col gap-3'>
              <span className='text-xl'>
                {template.name}
                <span className='ml-2.5 rounded-md border px-2 py-0.5 text-xs'>
                  Preview
                </span>
              </span>
            </Dialog.Title>
            <Dialog.Description className=''>
              <div className='border rounded-lg mt-4 p-4 bg-white max-w-full'>
                <div className='rounded-md overflow-auto'>
                  {isWhatsappLoading ? (
                    <Loading className='w-full h-48' />
                  ) : (
                    <>
                      <div
                        dangerouslySetInnerHTML={sanitizedContent ?? ''}
                      ></div>
                    </>
                  )}
                </div>
              </div>
            </Dialog.Description>
            <Dialog.Footer>
              <Button variant='primary' onClick={() => setShowPreview(false)}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      )}
    </>
  );
};

export default TemplatePreview;
